/**
 * Investment State Transitions and Validation Constants
 * Mirrors backend business logic for state transitions and document requirements
 *
 * Domain Layer - Pure business logic
 */

import type { InvestmentDocumentResponse } from "@/domain/documentBlocks";
import { ReviewStatus } from "@/domain/documentEnums";

import { InvestmentState, InvestmentDocumentType } from "./types";

// Valid state transitions (mirrored from backend)
export const VALID_TRANSITIONS: Record<InvestmentState, InvestmentState[]> = {
  [InvestmentState.PROSPECT]: [
    InvestmentState.RESERVED,
    InvestmentState.FINANCING,
    InvestmentState.FUNDED,
    InvestmentState.DECLARED,
    InvestmentState.CANCELLED,
    InvestmentState.REJECTED,
  ],
  [InvestmentState.RESERVED]: [
    InvestmentState.FINANCING,
    InvestmentState.FUNDED,
    InvestmentState.DECLARED,
    InvestmentState.CANCELLED,
    InvestmentState.EXPIRED,
    InvestmentState.REJECTED,
  ],
  [InvestmentState.FINANCING]: [
    InvestmentState.FUNDED,
    InvestmentState.DECLARED,
    InvestmentState.RESERVED, // Move back to reserved
    InvestmentState.PROSPECT, // Move back to prospect
    InvestmentState.CANCELLED, // Cancellation
    InvestmentState.REJECTED, // Rejection (KYC fail, etc)
  ],
  [InvestmentState.FUNDED]: [
    InvestmentState.DECLARED,
    InvestmentState.CANCELLED,
    InvestmentState.REJECTED,
  ],
  [InvestmentState.DECLARED]: [InvestmentState.REJECTED],
  [InvestmentState.CANCELLED]: [], // Terminal state
  [InvestmentState.EXPIRED]: [
    InvestmentState.RESERVED, // Can be renewed
    InvestmentState.CANCELLED,
  ],
  [InvestmentState.REJECTED]: [], // Terminal state
};

// Required documents for each state transition
// Note: All transitions to FUNDED require both FINANCING_CONTRACT and PAYMENT_RECEIPT (approved)
export const REQUIRED_DOCUMENTS: Record<string, InvestmentDocumentType[]> = {
  [`${InvestmentState.PROSPECT}-${InvestmentState.RESERVED}`]: [
    InvestmentDocumentType.INVESTMENT_PROPOSAL,
  ],
  [`${InvestmentState.RESERVED}-${InvestmentState.FINANCING}`]: [
    InvestmentDocumentType.RESERVATION_CONTRACT,
    InvestmentDocumentType.FINANCING_CONTRACT,
  ],
  // All transitions to FUNDED require financing contract + payment receipt
  [`${InvestmentState.PROSPECT}-${InvestmentState.FUNDED}`]: [
    InvestmentDocumentType.FINANCING_CONTRACT,
    InvestmentDocumentType.PAYMENT_RECEIPT,
  ],
  [`${InvestmentState.RESERVED}-${InvestmentState.FUNDED}`]: [
    InvestmentDocumentType.FINANCING_CONTRACT,
    InvestmentDocumentType.PAYMENT_RECEIPT,
  ],
  [`${InvestmentState.FINANCING}-${InvestmentState.FUNDED}`]: [
    InvestmentDocumentType.FINANCING_CONTRACT,
    InvestmentDocumentType.PAYMENT_RECEIPT,
  ],
  [`${InvestmentState.FUNDED}-${InvestmentState.DECLARED}`]: [
    InvestmentDocumentType.TAX_SUBMISSION_CERTIFICATE,
  ],
  // All transitions to DECLARED require financing contract + payment receipt + tax certificate
  [`${InvestmentState.PROSPECT}-${InvestmentState.DECLARED}`]: [
    InvestmentDocumentType.FINANCING_CONTRACT,
    InvestmentDocumentType.PAYMENT_RECEIPT,
    InvestmentDocumentType.TAX_SUBMISSION_CERTIFICATE,
  ],
  [`${InvestmentState.RESERVED}-${InvestmentState.DECLARED}`]: [
    InvestmentDocumentType.FINANCING_CONTRACT,
    InvestmentDocumentType.PAYMENT_RECEIPT,
    InvestmentDocumentType.TAX_SUBMISSION_CERTIFICATE,
  ],
  [`${InvestmentState.FINANCING}-${InvestmentState.DECLARED}`]: [
    InvestmentDocumentType.FINANCING_CONTRACT,
    InvestmentDocumentType.PAYMENT_RECEIPT,
    InvestmentDocumentType.TAX_SUBMISSION_CERTIFICATE,
  ],
};

// State labels and descriptions
export const STATE_LABELS: Record<InvestmentState, string> = {
  [InvestmentState.PROSPECT]: "Prospecto",
  [InvestmentState.RESERVED]: "Reservado",
  [InvestmentState.FINANCING]: "Financiando",
  [InvestmentState.FUNDED]: "Financiado",
  [InvestmentState.DECLARED]: "Comunicado",
  [InvestmentState.CANCELLED]: "Cancelado",
  [InvestmentState.EXPIRED]: "Expirado",
  [InvestmentState.REJECTED]: "Rechazado",
};

export const STATE_DESCRIPTIONS: Record<InvestmentState, string> = {
  [InvestmentState.PROSPECT]: "Interés inicial, sin compromiso formal",
  [InvestmentState.RESERVED]: "Cuota reservada temporalmente",
  [InvestmentState.FINANCING]: "Contratos de financiación firmados, pendiente de pago",
  [InvestmentState.FUNDED]: "Pago recibido y confirmado",
  [InvestmentState.DECLARED]: "Comunicado a las autoridades fiscales (terminal)",
  [InvestmentState.CANCELLED]: "Cancelado por el usuario",
  [InvestmentState.EXPIRED]: "Reserva expirada",
  [InvestmentState.REJECTED]: "Rechazado por validaciones",
};

// Action labels for transitions
export const TRANSITION_LABELS: Record<string, string> = {
  [`${InvestmentState.PROSPECT}-${InvestmentState.RESERVED}`]: "Reservar Cuota",
  [`${InvestmentState.PROSPECT}-${InvestmentState.FINANCING}`]: "Iniciar Financiación",
  [`${InvestmentState.PROSPECT}-${InvestmentState.FUNDED}`]: "Confirmar Pago",
  [`${InvestmentState.PROSPECT}-${InvestmentState.DECLARED}`]: "Declarar Inversión",
  [`${InvestmentState.PROSPECT}-${InvestmentState.CANCELLED}`]: "Cancelar Prospecto",
  [`${InvestmentState.PROSPECT}-${InvestmentState.REJECTED}`]: "Rechazar Prospecto",

  [`${InvestmentState.RESERVED}-${InvestmentState.FINANCING}`]: "Iniciar Financiación",
  [`${InvestmentState.RESERVED}-${InvestmentState.FUNDED}`]: "Confirmar Pago",
  [`${InvestmentState.RESERVED}-${InvestmentState.DECLARED}`]: "Declarar Inversión",
  [`${InvestmentState.RESERVED}-${InvestmentState.CANCELLED}`]: "Cancelar Reserva",
  [`${InvestmentState.RESERVED}-${InvestmentState.EXPIRED}`]: "Marcar como Expirado",
  [`${InvestmentState.RESERVED}-${InvestmentState.REJECTED}`]: "Rechazar Reserva",

  [`${InvestmentState.FINANCING}-${InvestmentState.FUNDED}`]: "Confirmar Pago",
  [`${InvestmentState.FINANCING}-${InvestmentState.DECLARED}`]: "Declarar Inversión",
  [`${InvestmentState.FINANCING}-${InvestmentState.RESERVED}`]: "Volver a Reservado",
  [`${InvestmentState.FINANCING}-${InvestmentState.PROSPECT}`]: "Volver a Prospecto",
  [`${InvestmentState.FINANCING}-${InvestmentState.CANCELLED}`]: "Cancelar Financiación",
  [`${InvestmentState.FINANCING}-${InvestmentState.REJECTED}`]: "Rechazar Financiación",

  [`${InvestmentState.FUNDED}-${InvestmentState.DECLARED}`]: "Declarar Inversión",
  [`${InvestmentState.FUNDED}-${InvestmentState.CANCELLED}`]: "Cancelar Inversión",
  [`${InvestmentState.FUNDED}-${InvestmentState.REJECTED}`]: "Rechazar Inversión",

  [`${InvestmentState.DECLARED}-${InvestmentState.REJECTED}`]: "Rechazar Declaración",

  [`${InvestmentState.EXPIRED}-${InvestmentState.RESERVED}`]: "Renovar Reserva",
  [`${InvestmentState.EXPIRED}-${InvestmentState.CANCELLED}`]: "Cancelar Definitivamente",
};

// Document type labels
export const DOCUMENT_LABELS: Record<InvestmentDocumentType, string> = {
  [InvestmentDocumentType.INVESTMENT_PROPOSAL]: "Propuesta de Inversión",
  [InvestmentDocumentType.RESERVATION_CONTRACT]: "Contrato de Reserva",
  [InvestmentDocumentType.FINANCING_CONTRACT]: "Contrato de Financiación",
  [InvestmentDocumentType.COMMUNICATION_CONTRACT]: "Contrato de Comunicación",
  [InvestmentDocumentType.PAYMENT_RECEIPT]: "Comprobante de Pago",
  [InvestmentDocumentType.TAX_SUBMISSION_CERTIFICATE]: "Justificante de Comunicación",
};

// Document type short labels (for badges)
export const DOCUMENT_SHORT_LABELS: Record<InvestmentDocumentType, string> = {
  [InvestmentDocumentType.INVESTMENT_PROPOSAL]: "Propuesta",
  [InvestmentDocumentType.RESERVATION_CONTRACT]: "Reserva",
  [InvestmentDocumentType.FINANCING_CONTRACT]: "Financiación",
  [InvestmentDocumentType.COMMUNICATION_CONTRACT]: "Comunicación",
  [InvestmentDocumentType.PAYMENT_RECEIPT]: "Pago",
  [InvestmentDocumentType.TAX_SUBMISSION_CERTIFICATE]: "Justificante",
};

// Color schemes for document types
// Using centralized color system from main.css
export const DOCUMENT_TYPE_COLORS: Record<
  InvestmentDocumentType,
  {
    bg: string;
    text: string;
  }
> = {
  [InvestmentDocumentType.INVESTMENT_PROPOSAL]: {
    bg: "bg-doctype-proposal",
    text: "text-doctype-proposal",
  },
  [InvestmentDocumentType.RESERVATION_CONTRACT]: {
    bg: "bg-doctype-reservation",
    text: "text-doctype-reservation",
  },
  [InvestmentDocumentType.FINANCING_CONTRACT]: {
    bg: "bg-doctype-financing",
    text: "text-doctype-financing",
  },
  [InvestmentDocumentType.COMMUNICATION_CONTRACT]: {
    bg: "bg-doctype-communication",
    text: "text-doctype-communication",
  },
  [InvestmentDocumentType.PAYMENT_RECEIPT]: {
    bg: "bg-doctype-payment",
    text: "text-doctype-payment",
  },
  [InvestmentDocumentType.TAX_SUBMISSION_CERTIFICATE]: {
    bg: "bg-doctype-tax-certificate",
    text: "text-doctype-tax-certificate",
  },
};

/**
 * Check if a state transition is valid
 */
export function canTransitionTo(
  currentState: InvestmentState,
  targetState: InvestmentState,
): boolean {
  return VALID_TRANSITIONS[currentState]?.includes(targetState) || false;
}

/**
 * Get all valid transitions from current state
 */
export function getValidTransitions(currentState: InvestmentState): InvestmentState[] {
  return VALID_TRANSITIONS[currentState] || [];
}

/**
 * Get required documents for a specific transition
 */
export function getRequiredDocuments(
  fromState: InvestmentState,
  toState: InvestmentState,
): InvestmentDocumentType[] {
  const key = `${fromState}-${toState}`;
  return REQUIRED_DOCUMENTS[key] || [];
}

/**
 * Get missing documents for a transition
 */
export function getMissingDocuments(
  fromState: InvestmentState,
  toState: InvestmentState,
  existingDocuments: InvestmentDocumentResponse[],
): InvestmentDocumentType[] {
  const required = getRequiredDocuments(fromState, toState);
  const existing = existingDocuments
    .filter((doc) => doc.review.status === ReviewStatus.APPROVED)
    .map((doc) => doc.document.doc_type as InvestmentDocumentType);

  return required.filter((docType) => !existing.includes(docType));
}

/**
 * Check if transition is allowed based on documents
 */
export function isTransitionAllowed(
  fromState: InvestmentState,
  toState: InvestmentState,
  existingDocuments: InvestmentDocumentResponse[],
): boolean {
  if (!canTransitionTo(fromState, toState)) {
    return false;
  }

  const missing = getMissingDocuments(fromState, toState, existingDocuments);
  return missing.length === 0;
}

/**
 * Check if reservation is expired
 */
export function isReservationExpired(reservationExpiresAt: string | null): boolean {
  if (!reservationExpiresAt) return false;
  return new Date(reservationExpiresAt) < new Date();
}

/**
 * Get transition label
 */
export function getTransitionLabel(fromState: InvestmentState, toState: InvestmentState): string {
  const key = `${fromState}-${toState}`;
  return TRANSITION_LABELS[key] || `Cambiar a ${STATE_LABELS[toState]}`;
}

/**
 * Get human-readable reason why transition is blocked
 */
export function getTransitionBlockedReason(
  fromState: InvestmentState,
  toState: InvestmentState,
  existingDocuments: InvestmentDocumentResponse[],
): string {
  if (!canTransitionTo(fromState, toState)) {
    return `No se puede transicionar de ${STATE_LABELS[fromState]} a ${STATE_LABELS[toState]}`;
  }

  const missing = getMissingDocuments(fromState, toState, existingDocuments);

  if (missing.length === 0) {
    return ""; // No reason - transition allowed
  }

  const missingLabels = missing.map((dt) => DOCUMENT_LABELS[dt]).join(", ");
  return `Faltan documentos requeridos: ${missingLabels}`;
}

/**
 * Unified transition error types
 */
export type TransitionErrorType =
  | "invalid_transition"
  | "missing_documents"
  | "invalid_source_state";

export interface TransitionError {
  type: TransitionErrorType;
  titleKey: string;
  messageKey: string;
  messageParams?: Record<string, string | number>;
  missingDocuments?: string[]; // Translation keys for missing documents
}

interface TransitionValid {
  valid: true;
}
interface TransitionInvalid {
  valid: false;
  error: TransitionError;
}
export type TransitionValidationResult = TransitionValid | TransitionInvalid;

/**
 * UNIFIED VALIDATION FUNCTION
 * Single source of truth for all transition validations and error messages
 *
 * This function validates state transitions and returns consistent error messages
 * for all cases: invalid transitions, missing documents, invalid source states
 */
export function validateTransition(
  fromState: InvestmentState,
  toState: InvestmentState,
  documents: InvestmentDocumentResponse[],
): TransitionValidationResult {
  const fromLabel = STATE_LABELS[fromState] || fromState;
  const toLabel = STATE_LABELS[toState] || toState;

  // 1. Check if transition is valid according to business rules
  if (!canTransitionTo(fromState, toState)) {
    return {
      valid: false,
      error: {
        type: "invalid_transition",
        titleKey: "investments.errors.transition.title_not_allowed",
        messageKey: "investments.errors.transition.cannot_move_from_to",
        messageParams: { from: fromLabel, to: toLabel },
      },
    };
  }

  // 2. Special validation for FUNDED/DECLARED states (requires cumulative documents)
  if (toState === InvestmentState.FUNDED || toState === InvestmentState.DECLARED) {
    // Define cumulative document requirements per target state
    const docRequirements: {
      type: InvestmentDocumentType;
      key: string;
    }[] = [
      {
        type: InvestmentDocumentType.FINANCING_CONTRACT,
        key: "investments.errors.documents.missing_financing_contract",
      },
      {
        type: InvestmentDocumentType.PAYMENT_RECEIPT,
        key: "investments.errors.documents.missing_payment_receipt",
      },
    ];

    if (toState === InvestmentState.DECLARED) {
      docRequirements.push({
        type: InvestmentDocumentType.TAX_SUBMISSION_CERTIFICATE,
        key: "investments.errors.documents.missing_tax_certificate",
      });
    }

    const missingDocs: string[] = [];
    for (const req of docRequirements) {
      const hasDoc = documents.some(
        (d) => d.document.doc_type === req.type && d.review.status === ReviewStatus.APPROVED,
      );
      if (!hasDoc) {
        missingDocs.push(req.key);
      }
    }

    if (missingDocs.length > 0) {
      return {
        valid: false,
        error: {
          type: "missing_documents",
          titleKey: "investments.errors.documents.title_multiple",
          messageKey: "investments.errors.documents.message_multiple",
          messageParams: { count: missingDocs.length },
          missingDocuments: missingDocs,
        },
      };
    }
  }

  // 3. Check for other document requirements (non-FUNDED transitions)
  const requiredDocs = getRequiredDocuments(fromState, toState);
  if (requiredDocs.length > 0) {
    const missingDocs = getMissingDocuments(fromState, toState, documents);

    if (missingDocs.length > 0) {
      const missingDocKeys = missingDocs.map((docType) => {
        // Map document types to translation keys
        const docKeyMap: Record<InvestmentDocumentType, string> = {
          [InvestmentDocumentType.INVESTMENT_PROPOSAL]:
            "investments.errors.documents.missing_investment_proposal",
          [InvestmentDocumentType.RESERVATION_CONTRACT]:
            "investments.errors.documents.missing_reservation_contract",
          [InvestmentDocumentType.FINANCING_CONTRACT]:
            "investments.errors.documents.missing_financing_contract",
          [InvestmentDocumentType.COMMUNICATION_CONTRACT]:
            "investments.errors.documents.missing_communication_contract",
          [InvestmentDocumentType.PAYMENT_RECEIPT]:
            "investments.errors.documents.missing_payment_receipt",
          [InvestmentDocumentType.TAX_SUBMISSION_CERTIFICATE]:
            "investments.errors.documents.missing_tax_certificate",
        };

        return docKeyMap[docType] || "investments.errors.documents.missing_document";
      });

      return {
        valid: false,
        error: {
          type: "missing_documents",
          titleKey: "investments.errors.documents.title_multiple",
          messageKey: "investments.errors.documents.message_multiple",
          messageParams: { count: missingDocKeys.length },
          missingDocuments: missingDocKeys,
        },
      };
    }
  }

  // All validations passed
  return { valid: true };
}

/**
 * @deprecated Use validateTransition() instead
 * Get formatted error message for invalid state transition
 */
export function getInvalidTransitionErrorMessage(
  fromState: InvestmentState,
  toState: InvestmentState,
): {
  titleKey: string;
  messageKey: string;
  messageParams: { from: string; to: string };
} {
  const fromLabel = STATE_LABELS[fromState] || fromState;
  const toLabel = STATE_LABELS[toState] || toState;

  return {
    titleKey: "investments.errors.transition.title_not_allowed",
    messageKey: "investments.errors.transition.cannot_move_from_to",
    messageParams: { from: fromLabel, to: toLabel },
  };
}

/**
 * @deprecated Use validateTransition() instead
 * Check if documents are sufficient for FUNDED transition
 */
export function canMoveToFunded(
  currentState: InvestmentState,
  documents: InvestmentDocumentResponse[],
): {
  allowed: boolean;
  reason?: string;
  reasons?: string[];
  translationKeys?: string[];
} {
  const validation = validateTransition(currentState, InvestmentState.FUNDED, documents);

  if (validation.valid) {
    return { allowed: true };
  }

  const { error } = validation;

  if (error.missingDocuments) {
    return {
      allowed: false,
      translationKeys: error.missingDocuments,
    };
  }

  return {
    allowed: false,
    translationKeys: [error.messageKey],
  };
}
